import { Component, ElementRef, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { ShareddtaService } from '@app/services/shareddta.service';
import { Location } from '@angular/common';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from '@app/services/dialog.service';
import { ToastService } from '@app/services/toast.service';
import { TicketsService } from '@app/services/tickets.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { BreadcrumbService } from '@app/services/breadcrumb.service';

interface TicketFormData {
  subject: string;
  description: string;
  attachments: File[];
  company_id?: string;
}
@Component({
  selector: 'app-create-ticket',
  templateUrl: './create-ticket.component.html',
  styleUrl: './create-ticket.component.scss'
})
export class CreateTicketComponent implements OnInit, AfterViewInit {
  ticketForm: TicketFormData = {
    subject: '',
    description: '',
    attachments: []
  };

  errors: { [key: string]: string } = {};

  subjectCounter = '0/100';
  descriptionCounter = '0/500';

  uploadedFiles: File[] = [];
  uploadedPreviews: string[] = [];
  MAX_FILES = 5;
  currentCategory: string | null = null;
  isReceivingAdmin: boolean = false;
  companyList: any[] = [];
  isLoadingCompanies: boolean = false;
  isLoading: boolean = false;

  constructor(public router: Router, private route: ActivatedRoute, private dialogService: DialogService, private toast: ToastService, private ticketService: TicketsService, private authService: AuthServiceService, private sharedData: ShareddtaService, private breadcrumbService: BreadcrumbService) { }

  ngAfterViewInit(): void {
    this.sharedData.signalPageReady();
  }

  ngOnInit(): void {
    this.breadcrumbService.clear();
    const isReceivingSide = this.router.url.includes('/receiving-admin');

    this.isReceivingAdmin = localStorage.getItem('is_receiving_admin') === 'true';
    this.route.queryParams.subscribe((params: any) => {
      if (params['category']) {
        this.currentCategory = params['category'];
      } else {
        this.currentCategory = 'po';
      }

      if (this.isReceivingAdmin && this.currentCategory === 'company') {
        this.fetchCompanies();
      }
    });
  }

  fetchCompanies(): void {
    this.isLoadingCompanies = true;
    this.ticketService.addressSearch(1); // Service updates its BehaviorSubject
    this.ticketService.getCompany().subscribe(
      (data: any) => {
        // Correctly extract result.results from the response
        this.companyList = data?.result?.results || [];
        this.isLoadingCompanies = false;
      },
      (error) => {
        this.isLoadingCompanies = false;
        console.error('Error fetching companies:', error);
      }
    );
  }

  onCategoryToggle(category: 'company' | 'po'): void {
    this.currentCategory = category;
    if (category === 'company' && this.companyList.length === 0) {
      this.fetchCompanies();
    }
    // Optionally reset company selection when switching to PO
    if (category === 'po') {
      this.ticketForm.company_id = undefined;
    }
  }

  updateSubjectCount(): void {
    const length = this.ticketForm.subject.length;
    this.subjectCounter = length + '/100';
    this.validateForm()
  }

  updateDescriptionCount(): void {
    const length = this.ticketForm.description.length;
    this.descriptionCounter = length + '/500';
    this.validateForm()
  }

  onFileSelect(event: any): void {
    const files = Array.from(event.target.files) as File[];

    if (this.uploadedFiles.length + files.length > this.MAX_FILES) {
      this.toast.show('error', `You can only upload maximum ${this.MAX_FILES} images`);;
      return;
    }

    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.uploadedFiles.push(file);
        this.displayImage(file);
      }
    });

    event.target.value = '';
  }

  displayImage(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.uploadedPreviews.push(e.target.result);
    };
    reader.readAsDataURL(file);
  }

  removeImage(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.uploadedPreviews.splice(index, 1);
  }

  openFileSelector(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  validateForm(): boolean {
    this.errors = {};
    let isValid = true;

    if (!this.ticketForm.subject.trim()) {
      this.errors['subject'] = 'Subject is required';
      isValid = false;
    }

    if (!this.ticketForm.description.trim()) {
      this.errors['description'] = 'Description is required';
      isValid = false;
    }

    if (this.isReceivingAdmin && this.currentCategory === 'company' && !this.ticketForm.company_id) {
      this.errors['company_id'] = 'Please select a company';
      isValid = false;
    }

    return isValid;
  }

  // createTicket(): void {
  //   if (!this.validateForm()) {
  //     return;
  //   }

  //   console.log('Ticket data:', {
  //     subject: this.ticketForm.subject,
  //     description: this.ticketForm.description,
  //     attachments: this.uploadedFiles.length
  //   });

  //   alert('Ticket created successfully!');

  //   // Here you would typically call your API service
  //   // this.ticketService.createTicket(this.ticketForm).subscribe(...)
  // }
  createTicket(): void {

    if (!this.validateForm()) {
      return;
    }

    const formData = new FormData();

    // API ke hisab se
    formData.append('subject', this.ticketForm.subject);
    formData.append('description', this.ticketForm.description);
    if (localStorage.getItem('is_receiving_admin') === 'true' && this.router.url.includes('/receiving-admin')) {
      formData.append('is_receiving_admin', 'true');
      if (this.currentCategory === 'company') {
        formData.append('ticket_type', 'nongeneral');
        if (this.ticketForm.company_id) {
          formData.append('schema', this.ticketForm.company_id);
        }
      } else if (this.currentCategory === 'po') {
        formData.append('ticket_type', 'general');
      }
    } else {
      formData.append('is_shipped_admin', 'true');
    }

    const imagePromises: Promise<void>[] = [];

    for (let i = 0; i < this.uploadedFiles.length; i++) {
      formData.append('images', this.uploadedFiles[i]);
    }

    Promise.all(imagePromises)
      .then(() => {
        this.isLoading = true;
        this.ticketService.createTicket(formData).subscribe(
          (data: any) => {
            this.isLoading = false;
            if (this.authService.resultCodeError(data)) {
              return;
            }
            this.toast.show('success', data.resultDescription);
            this.uploadedFiles = [];
            this.ticketForm.subject = '';
            this.ticketForm.description = '';
            this.resetForm();
            const basePath = this.router.url.includes('/receiving-admin') ? '/receiving-admin' : '/admin';
            this.router.navigate([`${basePath}/ticket`]);

          },
          (error) => {
            this.isLoading = false;
            this.authService.dataError(error);
          }
        );

      })
      .catch((error) => {
        console.error('Error converting images:', error);
      });
  }
  // cancel(): void {
  //   if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
  //     this.resetForm();
  //   }
  // }
  isFormDirty(): boolean {
    return (
      this.ticketForm.subject.trim() !== '' ||
      this.ticketForm.description.trim() !== '' ||
      this.uploadedFiles.length > 0 ||
      (this.isReceivingAdmin && this.ticketForm.company_id !== undefined)
    );
  }

  async cancel(): Promise<void> {
    if (!this.isFormDirty()) {
      this.navigateToTickets();
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Discard Changes',
      'Are you sure you want to cancel? Any unsaved changes will be lost.'
    );

    if (confirmed.confirmed) {
      this.navigateToTickets();
    }
  }

  private navigateToTickets(): void {
    const basePath = this.router.url.includes('/receiving-admin') ? '/receiving-admin' : '/admin';
    this.router.navigate([`${basePath}/ticket`]);
  }

  resetForm(): void {
    this.ticketForm = {
      subject: '',
      description: '',
      attachments: []
    };
    this.uploadedFiles = [];
    this.uploadedPreviews = [];
    this.errors = {};
    this.updateSubjectCount();
    this.updateDescriptionCount();
  }

  async goBack(): Promise<void> {
    if (!this.isFormDirty()) {
      window.history.back();
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Discard Changes',
      'Are you sure you want to cancel? Any unsaved changes will be lost.'
    );

    if (confirmed.confirmed) {
      window.history.back();
    }
  }
}